import _ from 'lodash';
import { useMemo } from 'react';
import { KEY_RECOIL } from '@/shared/config';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { selectIsLoadingHrChart, useReportStore } from '@/entities/report';
import type { SelectedDisclosureProps } from './index';

/**
 * Supplies the strips tab with the ECG disclosure hour currently selected and the means to jump the viewer to it. Reads the HR chart loading flag plus the stored and pending disclosure from the report store keyed to tab 3, prefers the pending ecgDisclosureChange when it is not empty, and exposes a jump action writing the disclosure start as the jump date along with a range formatter.
 *
 * @param props - Hook props carrying the timezoneOffset used when formatting the disclosure range.
 * @returns An object carrying isLoadingHrChart, selectedEcgDisclosure, onClickJumpTo and generateEcgDisclosureTime.
 */
const useSelectedDisclosure = ({ timezoneOffset }: SelectedDisclosureProps) => {
  const keyRecoil = KEY_RECOIL.STRIP_MANAGEMENT;

  const isLoadingHrChart = useReportStore(selectIsLoadingHrChart(keyRecoil));
  const ecgDisclosure = useReportStore((s) => s.selectedEcgDisclosure);
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Writes a jump target into the report store for the strips tab. Calls the store's setJumpDate action with the tab 3 key and the supplied value.
   *
   * @param v - The jump date value forwarded to the store action.
   * @returns Nothing.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const ecgDisclosureChange = useReportStore((s) => s.ecgDisclosureChange);

  const selectedEcgDisclosure = useMemo(() => {
    if (!_.isEmpty(ecgDisclosureChange)) return ecgDisclosureChange;
    return ecgDisclosure;
  }, [ecgDisclosure, ecgDisclosureChange]);

  /**
   * Moves the ECG viewer to the start of the selected disclosure hour. Writes the selected disclosure's start timestamp into the report store's jump date for tab 3.
   *
   * @returns Nothing.
   */
  const onClickJumpTo = () => {
    setJumpDate(selectedEcgDisclosure.start);
  };

  /**
   * Formats the one-hour window covered by a selected ECG disclosure page. Converts the disclosure start to report local time with the timezone offset, adds one hour for the end, and joins the full start date-time with the end time.
   *
   * @param ecgDisclosure - Disclosure record read for its start timestamp.
   * @returns The disclosure range formatted as MM/DD/YYYY, HH:mm:ss - HH:mm:ss.
   */
  const generateEcgDisclosureTime = (ecgDisclosure: any) => {
    const { start } = ecgDisclosure;
    const stopString = convertToMomentJs(start, timezoneOffset).add(1, 'hours').format('HH:mm:ss');
    return `${convertToMomentJs(start, timezoneOffset).format('MM/DD/YYYY, HH:mm:ss')} - ${stopString}`;
  };

  return {
    isLoadingHrChart,
    selectedEcgDisclosure,
    onClickJumpTo,
    generateEcgDisclosureTime,
  };
};

export default useSelectedDisclosure;
